import React, { useLayoutEffect, useRef, useState } from 'react';
import { animate } from 'framer-motion';
import { useForkRef } from '@mui/material/utils';
import type { TransitionProps } from '@mui/material/transitions';
import { exitTween, SPRING } from './motionTokens';

// App-wide dropdown motion. Every menu, select list, popover, autocomplete list and custom
// dropdown grows out from its trigger the same way: a clip mask opens from the trigger's
// side (content is revealed, never scaled or squashed), the surface settles a few px on the
// shared `morph` spring, and the first items blur in on a short stagger. Visual styles
// (colors, radius, glass, size) stay whatever each dropdown already has.

const SHADOW_ROOM_PX = 40;
const SIDE_INSET = 0.12;
const MAX_STAGGERED_ITEMS = 8;
const ITEM_SELECTOR = '[role="menuitem"], [role="option"], [role="menuitemradio"], [role="menuitemcheckbox"], [data-motion-item]';

type Running = { stop: () => void };

const prefersReducedMotion = () => (
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

// jsdom (unit tests) has no layout and no Web Animations; skip motion there.
const canAnimate = (node: HTMLElement) => typeof node.animate === 'function';

const radiusOf = (node: HTMLElement) => {
  const value = parseFloat(window.getComputedStyle(node).borderTopLeftRadius);
  return Number.isFinite(value) ? value : 0;
};

// Popover records where the surface is anchored in transform-origin and popper.js records
// the placement on its root; a dropdown that opened upward grows from its bottom edge.
const opensUpward = (node: HTMLElement) => {
  const y = (node.style.transformOrigin || '').split(' ')[1];
  if (y === 'bottom') return true;
  if (y && y.endsWith('px')) {
    const px = parseFloat(y);
    return Number.isFinite(px) && px > node.offsetHeight / 2;
  }
  const placement = node.closest('[data-popper-placement]')?.getAttribute('data-popper-placement');
  return Boolean(placement?.startsWith('top'));
};

// Which edge the surface grows from: the side its trigger is on.
export type GrowOrigin = 'auto' | 'top' | 'bottom' | 'left' | 'right';
type Edge = Exclude<GrowOrigin, 'auto'>;

const resolveEdge = (node: HTMLElement, origin: GrowOrigin): Edge => (
  origin === 'auto' ? (opensUpward(node) ? 'bottom' : 'top') : origin
);

// Closed mask: a thin band at the trigger's edge. Sideways origins start as a square the
// height of the surface, so a bar grows out of the round button at its end.
const clipClosed = (node: HTMLElement, edge: Edge) => {
  const w = node.offsetWidth;
  const h = node.offsetHeight;
  const side = Math.round(w * SIDE_INSET);
  const r = radiusOf(node);
  switch (edge) {
    case 'bottom': return `inset(${h}px ${side}px 0px ${side}px round ${r}px)`;
    case 'right': return `inset(0px 0px 0px ${Math.max(0, w - h)}px round ${r}px)`;
    case 'left': return `inset(0px ${Math.max(0, w - h)}px 0px 0px round ${r}px)`;
    default: return `inset(0px ${side}px ${h}px ${side}px round ${r}px)`;
  }
};

const nudge = (edge: Edge, px: number) => {
  switch (edge) {
    case 'bottom': return `0px ${px}px`;
    case 'right': return `${px}px 0px`;
    case 'left': return `${-px}px 0px`;
    default: return `0px ${-px}px`;
  }
};
// Negative insets leave room for the surface's own shadow while the mask is still applied.
const clipOpen = (node: HTMLElement) => {
  const room = -SHADOW_ROOM_PX;
  return `inset(${room}px ${room}px ${room}px ${room}px round ${radiusOf(node)}px)`;
};

const staggerTargets = (node: HTMLElement) => (
  Array.from(node.querySelectorAll<HTMLElement>(ITEM_SELECTOR))
    // Disabled options retain their CSS opacity throughout the surface reveal.
    // Animating them to 1 then clearing it produces a bright-to-faint flash.
    .filter(item => item.getAttribute('aria-disabled') !== 'true' && !item.hasAttribute('disabled'))
    .slice(0, MAX_STAGGERED_ITEMS)
);

const clearMotionStyles = (node: HTMLElement) => {
  node.style.scale = '';
  node.style.clipPath = '';
  node.style.translate = '';
  node.style.opacity = '';
  node.style.filter = '';
};

const noop: Running = { stop: () => undefined };

type StyleProp = 'opacity' | 'clipPath' | 'translate' | 'filter' | 'scale';
type Track = [StyleProp, string | number, string | number, any];

// Each value runs as a plain value animation that writes the style itself every frame.
// Animating the element directly lets framer hand opacity/clip/filter to WAAPI, which on
// finish leaves one frame showing the starting value (the dropdown blinks out once).
const drive = (node: HTMLElement, tracks: Track[]) => {
  const controls = tracks.map(([prop, from, to, transition]) => {
    node.style[prop] = String(from);
    return animate(from as any, to as any, {
      ...transition,
      onUpdate: (value: string | number) => { node.style[prop] = String(value); },
    });
  });
  return {
    finished: Promise.all(controls.map(control => new Promise<void>(resolve => { control.then(() => resolve()); }))),
    stop: () => controls.forEach(control => control.stop()),
  };
};

export const revealIn = (node: HTMLElement, onDone?: () => void, origin: GrowOrigin = 'auto'): Running => {
  if (!canAnimate(node)) {
    onDone?.();
    return noop;
  }
  if (prefersReducedMotion()) {
    const fade = drive(node, [['opacity', 0, 1, { duration: 0.12 }]]);
    fade.finished.then(() => { clearMotionStyles(node); onDone?.(); });
    return fade;
  }
  const edge = resolveEdge(node, origin);
  const items = staggerTargets(node);
  // Hidden state applied synchronously so the first painted frame is already closed.
  items.forEach(item => { item.style.opacity = '0'; });
  const surface = drive(node, [
    ['clipPath', clipClosed(node, edge), clipOpen(node), SPRING.morph],
    ['translate', nudge(edge, 6), '0px 0px', SPRING.morph],
    ['opacity', 0, 1, { duration: 0.12 }],
  ]);
  const itemAnimations = items.map((item, index) => {
    const delay = { ...SPRING.snap, delay: 0.06 + index * 0.03 };
    const control = drive(item, [
      ['opacity', 0, 1, delay],
      ['filter', 'blur(4px)', 'blur(0px)', delay],
      ['translate', nudge(edge, 4), '0px 0px', delay],
    ]);
    control.finished.then(() => clearMotionStyles(item));
    return control;
  });
  surface.finished.then(() => {
    clearMotionStyles(node);
    onDone?.();
  });
  return {
    stop: () => {
      surface.stop();
      itemAnimations.forEach(control => control.stop());
      clearMotionStyles(node);
      items.forEach(clearMotionStyles);
    },
  };
};

export const revealOut = (node: HTMLElement, onDone?: () => void, origin: GrowOrigin = 'auto'): Running => {
  if (!canAnimate(node)) {
    onDone?.();
    return noop;
  }
  if (prefersReducedMotion()) {
    const fade = drive(node, [['opacity', 1, 0, { duration: 0.1 }]]);
    fade.finished.then(() => onDone?.());
    return fade;
  }
  const edge = resolveEdge(node, origin);
  const tween = exitTween(0.14);
  const out = drive(node, [
    ['clipPath', clipOpen(node), clipClosed(node, edge), tween],
    ['translate', '0px 0px', nudge(edge, 4), tween],
    ['opacity', 1, 0, tween],
  ]);
  // Every frame (including the last) is written by onUpdate, so the closed state stays
  // applied until MUI unmounts the surface.
  out.finished.then(() => onDone?.());
  return out;
};

// Popper sites clear their anchor on close; holding the last one lets the panel play its
// exit in place instead of jumping to the corner while it fades.
export const useLatchedAnchor = <T,>(anchor: T | null): T | null => {
  const last = useRef<T | null>(anchor);
  if (anchor) last.current = anchor;
  return anchor ?? last.current;
};

type MotionGrowProps = TransitionProps & {
  children: React.ReactElement<any, any>;
  // For custom (non-MUI) dropdowns: render nothing once fully closed.
  unmountOnExit?: boolean;
  origin?: GrowOrigin;
};

// Drop-in for MUI's TransitionComponent (Menu, Popover, Select, Popper `transition`), and
// usable directly around a custom dropdown panel: <MotionGrow in={open} unmountOnExit>.
const MotionGrow = React.forwardRef<HTMLElement, MotionGrowProps>(function MotionGrow(props, ref) {
  const {
    in: inProp = false,
    appear = true,
    children,
    onEnter,
    onEntering,
    onEntered,
    onExit,
    onExiting,
    onExited,
    unmountOnExit = false,
    origin = 'auto',
    style,
    // Transition-only props MUI passes that the surface must not receive.
    timeout: _timeout,
    easing: _easing,
    addEndListener: _addEndListener,
    mountOnEnter: _mountOnEnter,
    enter: _enter,
    exit: _exit,
    ...passThrough
  } = props as MotionGrowProps & Record<string, any>;
  const nodeRef = useRef<HTMLElement | null>(null);
  const handleRef = useForkRef(nodeRef, (children as any).ref, ref);
  const [exited, setExited] = useState(!inProp);
  const running = useRef<Running | null>(null);
  const hasRun = useRef(false);

  if (inProp && exited) setExited(false);

  useLayoutEffect(() => {
    const node = nodeRef.current;
    const isFirst = !hasRun.current;
    hasRun.current = true;
    if (!node) return undefined;
    running.current?.stop();
    if (inProp) {
      onEnter?.(node, isFirst);
      onEntering?.(node, isFirst);
      if (isFirst && !appear) {
        onEntered?.(node, isFirst);
        return undefined;
      }
      running.current = revealIn(node, () => onEntered?.(node, isFirst), origin);
    } else if (!isFirst) {
      onExit?.(node);
      onExiting?.(node);
      running.current = revealOut(node, () => {
        setExited(true);
        onExited?.(node);
      }, origin);
    }
    return () => running.current?.stop();
    // Callbacks change identity every render; only `in` drives the transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProp]);

  if (unmountOnExit && exited && !inProp) return null;

  // Anything else (e.g. ClickAwayListener's capture handlers) goes on to the surface.
  return React.cloneElement(children, {
    ...passThrough,
    ref: handleRef,
    style: {
      visibility: exited && !inProp ? 'hidden' : undefined,
      ...style,
      ...children.props.style,
    },
  });
});

export default MotionGrow;

// Opening-only variant for lists that empty themselves the moment they close (autocomplete
// results, search suggestions): an exit would flash an empty or "No options" surface.
export const RevealOnMount = React.forwardRef<HTMLElement, { children: React.ReactElement<any, any>; origin?: GrowOrigin }>(
  function RevealOnMount({ children, origin = 'auto' }, ref) {
    const nodeRef = useRef<HTMLElement | null>(null);
    const handleRef = useForkRef(nodeRef, (children as any).ref, ref);
    useLayoutEffect(() => {
      if (!nodeRef.current) return undefined;
      const running = revealIn(nodeRef.current, undefined, origin);
      return () => running.stop();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return React.cloneElement(children, { ref: handleRef });
  },
);

// Dialogs (MUI Dialog's TransitionComponent, set app-wide in the theme): the centred surface
// grows in from slightly smaller on the `morph` spring as it fades in, and leaves quickly.
const MotionPop = React.forwardRef<HTMLElement, TransitionProps & { children: React.ReactElement<any, any> }>(
  function MotionPop(props, ref) {
    const {
      in: inProp = false,
      appear = true,
      children,
      onEnter,
      onEntering,
      onEntered,
      onExit,
      onExiting,
      onExited,
      style,
      timeout: _timeout,
      easing: _easing,
      addEndListener: _addEndListener,
      mountOnEnter: _mountOnEnter,
      unmountOnExit: _unmountOnExit,
      enter: _enter,
      exit: _exit,
      ...passThrough
    } = props as TransitionProps & { children: React.ReactElement<any, any> } & Record<string, any>;
    const nodeRef = useRef<HTMLElement | null>(null);
    const handleRef = useForkRef(nodeRef, (children as any).ref, ref);
    const running = useRef<{ stop: () => void } | null>(null);
    const hasRun = useRef(false);
    const [exited, setExited] = useState(!inProp);
    if (inProp && exited) setExited(false);

    useLayoutEffect(() => {
      const node = nodeRef.current;
      const isFirst = !hasRun.current;
      hasRun.current = true;
      if (!node) return undefined;
      running.current?.stop();
      if (inProp) {
        onEnter?.(node, isFirst);
        onEntering?.(node, isFirst);
        if ((isFirst && !appear) || !canAnimate(node)) {
          onEntered?.(node, isFirst);
          return undefined;
        }
        const reduced = prefersReducedMotion();
        const enter = drive(node, reduced
          ? [['opacity', 0, 1, { duration: 0.12 }]]
          : [
            ['opacity', 0, 1, { duration: 0.16 }],
            ['scale', 0.96, 1, SPRING.morph],
            ['translate', '0px 8px', '0px 0px', SPRING.morph],
          ]);
        enter.finished.then(() => { clearMotionStyles(node); onEntered?.(node, isFirst); });
        running.current = enter;
      } else if (!isFirst) {
        onExit?.(node);
        onExiting?.(node);
        if (!canAnimate(node)) {
          setExited(true);
          onExited?.(node);
          return undefined;
        }
        const tween = exitTween(0.14);
        const leave = drive(node, [
          ['opacity', 1, 0, tween],
          ['scale', 1, 0.97, tween],
        ]);
        leave.finished.then(() => { setExited(true); onExited?.(node); });
        running.current = leave;
      }
      return () => running.current?.stop();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inProp]);

    return React.cloneElement(children, {
      ...passThrough,
      ref: handleRef,
      style: { visibility: exited && !inProp ? 'hidden' : undefined, ...style, ...children.props.style },
    });
  },
);

export { MotionPop };
